import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  InputBase,
  Typography,
  IconButton,
  Badge,
  CircularProgress,
} from "@mui/material";
import EnhancedTable from "@components/Table/TableView";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import type { AppDispatch, RootState } from "@store/store";
import { useDispatch, useSelector } from "react-redux";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ArticleIcon from "@mui/icons-material/Article";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import dayjs from "dayjs";
import { TicketFilterDialog } from "./components/TicketFilterDialog";
import CreateTicketModal from "./components/CreateTicketModal";
import {
  setSearchTerm,
  setPage,
  setFilters,
  resetFilters,
  invalidateTicketsCache,
} from "./redux/ticketSlice";
import { fetchTickets } from "./redux/thunk/ticketThunk";

interface StatusStyle {
  text: string;
  bg: string;
}

interface FilterState {
  status: string;
  fromDate: dayjs.Dayjs | null;
  toDate: dayjs.Dayjs | null;
}

export const HelpDesk: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { profile } = useSelector((state: RootState) => state.profile);
  const {
    tickets,
    statusList,
    totalItems,
    currentPage: page,
    rowsPerPage,
    searchTerm,
    loading,
    filters,
  } = useSelector((state: RootState) => state.tickets);
  const [filterOpen, setFilterOpen] = useState(false);
  const [modalCreateOpen, setModalCreateOpen] = useState(false);

  const cleanMobileNumber = React.useMemo(
    () => profile?.mobile_number?.replace(/^\+91\s?/, '') || '',
    [profile?.mobile_number]
  );

  const renderStatusBadge = (status: string): any => {
    const statusStyles: Record<string, StatusStyle> = {
      Pending: { text: "#9F7803", bg: "#FFF3CD" },
      Closed: { text: "#374151", bg: "#F3F4F6" },
      Resolved: { text: "#4CAF50", bg: "#DBEFDC" },
      Open: { text: "#2196F3", bg: "#D3EAFD" },
      "In Progress": { text: "#9F7803", bg: "#FFF3CD" },
      Escalated: { text: "#B3261D", bg: "#F0D4D2" },
      "Inspection Pending": { text: "#9F7803", bg: "#FFF3CD" },
      "Visit Scheduled": { text: "#0471A2", bg: "#D4F0FD" },
      "Report Submitted": { text: "#4CAF50", bg: "#DBEFDC" },
      "Recommended to CA": { text: "#2196F3", bg: "#D3EAFD" },
      "Application Rejected": { text: "#B3261D", bg: "#F0D4D2" },
      "Payment Pending": { text: "#B86D00", bg: "#FFEACC" },
      "Payment Received": { text: "#43A047", bg: "#D9ECDA" },
      "Forwarded to Competent Authority": { text: "#1976D2", bg: "#D1E4F6" },
      "Provisional License Issued": { text: "#6FA135", bg: "#E2ECD7" },
      "EOT Requested": { text: "#AB47BC", bg: "#EEDAF2" },
      "EOT Approved": { text: "#6A1B9A", bg: "#E1D1EB" },
      "EOT Rejected": { text: "#E53935", bg: "#FAD7D7" },
      "Clarification Requested": { text: "#FF7043", bg: "#FFE2D9" },
      "Clarification Updated": { text: "#26A69A", bg: "#D4EDEB" },
      "Final License Issued": { text: "#2E7D32", bg: "#D5E5D6" },
      "Application Submitted": { text: "#2196F3", bg: "#D3EAFD" },
      Draft: { text: "#7A7A7A", bg: "#9E9E9E33" },
      "Scrutiny Pending": { text: "#9F7803", bg: "#FFF3CD" },
      "BG Forfeiture Initiated": { text: "#B3261D", bg: "#F0D4D2" }
    };

    const style = statusStyles[status] || { bg: "#f3f4f6", text: "#374151" };

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          borderRadius: "3px",
          fontSize: "0.875rem",
          fontWeight: 500,
          bgcolor: style.bg,
          color: style.text
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: 4,
            height: 4,
            borderRadius: "50%",
            bgcolor: style.text
          }}
        ></Box>
        {status}
      </Box>
    );
  };

  const headCells = [
    { key: "application_code", label: "Ticket ID", sort: true },
    { key: "category", label: "Category", sort: true },
    { key: "query", label: "Query", sort: true },
    { key: "created_on", label: "Created on", sort: true },
    { key: "updated_on", label: "Last updated on", sort: true },
    { key: "status_name", label: "Status", sort: true },
    { key: "action", label: "Actions", sort: false }
  ];

  const selectedColumns = headCells.map(col => col.key);

  // Transform tickets data for table
  const filtered = tickets.map(item => ({
    application_code: item.ticketNumber || "-",
    category: item.issueType?.name ?? "-",
    query: item.issueDetails?.issueDetails ?? "-",
    created_on: item.createdAt || new Date().toISOString(),
    updated_on: item.createdAt || new Date().toISOString(),
    status_name: item.status?.name ?? "-"
  }));

  // Paginate the data
  const paginated = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Format rows with JSX elements
  const rowsWithAction: any[] = paginated.map((obj, index) => {
    const ticketData = tickets[((page - 1) * rowsPerPage) + index];

    return {
      ...obj,
      category: <Box sx={{ textAlign: "left" }}>{obj.category}</Box>,
      query: (
        <Box
          sx={{
            textAlign: "left",
            maxWidth: "300px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {obj.query}
        </Box>
      ),
      created_on: obj.created_on ? dayjs(obj.created_on).format("DD MMM'YY, h:mmA") : "-",
      updated_on: obj.updated_on ? dayjs(obj.updated_on).format("DD MMM'YY, h:mmA") : "-",
      status_name: renderStatusBadge(obj.status_name),
      action: (
        <Box sx={{ textAlign: "center" }}>
          <IconButton
            aria-label="view ticket details"
            size="small"
            onClick={() => navigate(`/ticket-detail/${ticketData?.ticketID}`)}
            sx={{
              color: "#4A2CA9",
              "&:hover": { bgcolor: "#F8F7FC" }
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </Box>
      )
    };
  });


  const handleChangeTablePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeTableSort = () => {
  };

  // Fetch tickets when dependencies change
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchTickets({
        page,
        rowsPerPage,
        searchTerm,
        filters,
        mobile: cleanMobileNumber,
      }));
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(timer);
  }, [page, rowsPerPage, searchTerm, filters, cleanMobileNumber, dispatch]);



  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.status) count++;
    if (filters.fromDate) count++;
    if (filters.toDate) count++;
    return count;
  };


  // Handle search term change
  const handleSearchChange = (value: string) => {
    dispatch(setSearchTerm(value));
    dispatch(setPage(1)); // Reset to first page on search
  };

  // Handle filter apply
  const handleApplyFilters = (newFilters: FilterState) => {
    dispatch(setFilters({
      status: newFilters.status,
      fromDate: newFilters.fromDate ? newFilters.fromDate.format("YYYY-MM-DD") : null,
      toDate: newFilters.toDate ? newFilters.toDate.format("YYYY-MM-DD") : null,
    }));
     dispatch(setPage(1)); // Reset to first page on filter
    setFilterOpen(false);
  };

  // Handle filter reset
  const handleResetFilters = () => {
    dispatch(resetFilters());
    dispatch(setPage(1)); // Reset to first page on filter reset
    setFilterOpen(false);
  };

  // Handle ticket creation success
   const handleTicketCreated = () => {
    dispatch(setPage(1)); // Reset to first page
    dispatch(fetchTickets({
      page: 1,
      rowsPerPage,
      searchTerm,
      filters,
      mobile: cleanMobileNumber,
    }));
    setModalCreateOpen(false);
  };


  return (
    <Box className="app-details-wrapper">
      <div className="page-content">
        <>
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "12px",
              p: 2.5,
              mb: 3,
              border: "1px solid #EEEBF7"
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <Typography
                sx={{
                  textAlign: "left",
                  fontWeight: 600,
                  fontSize: 20,
                  color: "#1F2937"
                }}
              >
                My Tickets
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/faq")}
                  sx={{
                    borderColor: "#D9D2F1",
                    color: "#4A2CA9",
                    textTransform: "none",
                    borderRadius: "999px",
                    px: 2,
                    py: 0.75,
                    bgcolor: "#fff",
                    "&:hover": {
                      borderColor: "#C9C0ED",
                      bgcolor: "#F8F7FC"
                    }
                  }}
                  startIcon={
                    <ArticleIcon sx={{ fontSize: 18 }} />
                  }
                >
                  Visit FAQ
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setModalCreateOpen(true)}
                  sx={{
                    bgcolor: "#3A1C73",
                    color: "#fff",
                    textTransform: "none",
                    borderRadius: "999px",
                    px: 2,
                    py: 0.75,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#3E2490" }
                  }}
                  startIcon={<AddOutlinedIcon sx={{ color: "#fff" }} />}
                >
                  Create New Ticket
                </Button>
              </Box>
            </Box>
          </Box>

          <Box className="w-full bg-gray-000 rounded-lg p-4">
            {/* Search + Filter row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 3
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    border: "1px solid #dee1e6",
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.75,
                    minWidth: 280,
                    bgcolor: "#fff"
                  }}
                >
                  <SearchIcon sx={{ color: "#6B7280" }} />
                  <InputBase
                    placeholder="Search tickets"
                    value={searchTerm}
                    onChange={e => handleSearchChange(e.target.value)}
                    sx={{ width: "100%" }}
                  />
                </Box>

                <IconButton
                  aria-label="filter"
                  onClick={() => setFilterOpen(true)}
                  sx={{
                    border: "1px solid #D6DFE6",
                    borderRadius: "12px",
                    width: 40,
                    height: 40,
                    bgcolor: getActiveFilterCount() > 0 ? "#F3F0FF" : "#fff",
                    boxShadow: "none",
                    position: "relative",
                    p: 0,
                    "&:hover": {
                      bgcolor: "#F9FAFB",
                    },
                  }}
                >
                  <Badge
                    badgeContent={getActiveFilterCount()}
                    color="error"
                    overlap="circular"
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "#B3261D",
                        color: "#fff",
                        fontWeight: 700,
                        borderRadius: "50%",
                        minWidth: 20,
                        height: 20,
                        lineHeight: "20px",
                        fontSize: "0.8rem",
                        border: "2px solid #fff",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        margin: "-10px -10px -3px 0px"
                      }
                    }}
                  >
                    <FilterAltIcon
                      sx={{
                        color: getActiveFilterCount() > 0 ? "#3A1C73" : "#1F2937",
                        fontSize: 24,
                      }}
                    />
                  </Badge>
                </IconButton>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <EnhancedTable
                tableColumns={headCells.filter(obj =>
                  selectedColumns.includes(obj.key)
                )}
                tablerows={rowsWithAction.map(obj => {
                  const row: Record<string, any> = {};
                  Object.entries(obj).forEach(([key, value]) => {
                    if (selectedColumns.includes(key)) {
                      row[key] = value;
                    }
                  });
                  return row;
                })}
                handleChangeTablePage={handleChangeTablePage}
                handleChangeTableSort={handleChangeTableSort}
                page={page}
                rowsPerPage={rowsPerPage}
                count={totalItems}
              />
            )}
          </Box>
        </>
        {/* Filter Modal/Dialog */}
        <TicketFilterDialog
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          activeFilters={{
            status: filters.status,
            fromDate: filters.fromDate ? dayjs(filters.fromDate) : null,
            toDate: filters.toDate ? dayjs(filters.toDate) : null,
          }}
          statusList={statusList}
        />
        <CreateTicketModal
          open={modalCreateOpen}
          onClose={() => setModalCreateOpen(false)}
          onSuccess={handleTicketCreated}
        />
      </div>
    </Box>
  );
};